import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Wallet, Loader2, ShieldCheck, ExternalLink, RotateCw, AlertTriangle } from 'lucide-react';

/**
 * XamanConnectDialog
 *
 * One-tap wallet linking via Xaman Sign-In (no transaction, no XRP needed).
 *
 * Flow:
 *   1. Backend creates a SignIn payload → returns { uuid, deeplink, qr_png }
 *   2. We render the QR + "Open in Xaman" button.
 *   3. We poll /status every 3s until signed/expired/rejected.
 *   4. On signed, backend auto-links (or auto-reclaims if the wallet was on another account).
 */
export default function XamanConnectDialog({ open, onOpenChange, onSuccess }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | creating | waiting | completed | failed
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const cleanup = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Reset on close
  useEffect(() => {
    if (!open) {
      cleanup();
      setSession(null);
      setStatus('idle');
      setResult(null);
      setError('');
    }
  }, [open]);

  // Sequence number to re-trigger the auto-start effect for retries.
  const [seq, setSeq] = useState(0);

  // Auto-start when dialog opens (or when retry bumps `seq`).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setStatus('creating');
      setError('');
      try {
        const r = await axios.post(`${API}/wallet/xaman/start`);
        if (cancelled) return;
        setSession(r.data);
        setStatus('waiting');
        beginPolling(r.data.uuid);
      } catch (e) {
        if (cancelled) return;
        setStatus('failed');
        setError(e.response?.data?.detail || 'Could not start Xaman sign-in');
      }
    })();
    return () => { cancelled = true; };
  }, [open, seq]);

  const handleRetry = () => setSeq((s) => s + 1);

  const beginPolling = (uuid) => {
    cleanup();
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/wallet/xaman/status`, { params: { uuid } });
        const s = r.data?.status;
        if (s === 'completed') {
          cleanup();
          setStatus('completed');
          setResult(r.data);
          toast.success(r.data.action === 'reclaimed'
            ? `Wallet reclaimed from previous account`
            : (r.data.action === 'already_yours' ? 'Wallet already linked' : 'Wallet linked!'));
          onSuccess?.(r.data.xrpl_address);
        } else if (s === 'expired') {
          cleanup();
          setStatus('failed');
          setError('Sign-in window expired. Tap retry.');
        } else if (s === 'rejected' || s === 'cancelled') {
          cleanup();
          setStatus('failed');
          setError('Sign-in was rejected in Xaman.');
        }
      } catch (e) {
        // transient — keep polling
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="xaman-connect-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-400" /> Connect with Xaman
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            One tap. No fees. No transaction. Just sign in.
          </DialogDescription>
        </DialogHeader>

        {status === 'creating' && (
          <div className="text-center py-10">
            <Loader2 className="w-7 h-7 mx-auto text-amber-400 animate-spin mb-2" />
            <p className="text-xs text-slate-400">Creating sign-in request…</p>
          </div>
        )}

        {status === 'waiting' && session && (
          <div className="space-y-3">
            <div className="bg-white rounded-xl p-3 flex flex-col items-center">
              <img
                data-testid="xaman-qr"
                src={session.qr_png}
                alt="Scan with Xaman"
                className="w-44 h-44"
              />
              <p className="text-[10px] text-slate-700 mt-2">Scan with the Xaman app</p>
            </div>

            <a
              data-testid="xaman-open-deeplink"
              href={session.deeplink}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-blue-500 hover:bg-blue-400 text-white font-bold text-center py-2.5 rounded-md text-sm transition"
            >
              <span className="inline-flex items-center gap-1.5">
                <ExternalLink className="w-4 h-4" /> Open in Xaman
              </span>
            </a>

            <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Waiting for you to sign in Xaman…
            </div>

            <p className="text-[10px] text-slate-600 text-center">
              You'll see a sign-in request — just tap Allow. No XRP is sent.
            </p>
          </div>
        )}

        {status === 'completed' && result && (
          <div className="space-y-3">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
              <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-1" />
              <p className="text-sm font-semibold text-emerald-300">
                {result.action === 'reclaimed' ? 'Wallet Reclaimed' :
                  result.action === 'already_yours' ? 'Already Linked' : 'Wallet Linked'}
              </p>
              <code data-testid="xaman-linked-address" className="text-[10px] text-slate-400 break-all block mt-1">
                {result.xrpl_address}
              </code>
              {result.from_user && (
                <p className="text-[10px] text-amber-300 mt-1">
                  Moved from @{result.from_user}
                </p>
              )}
            </div>
            <Button
              data-testid="xaman-close-success"
              onClick={() => onOpenChange(false)}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold h-10"
            >
              Done
            </Button>
          </div>
        )}

        {status === 'failed' && (
          <div className="space-y-3">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-1" />
              <p data-testid="xaman-error-msg" className="text-sm text-red-300">{error}</p>
            </div>
            <Button
              data-testid="xaman-retry"
              onClick={handleRetry}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-10"
            >
              <RotateCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
